/**
 * MarketFormDialog — RHF+Zod create/edit dialog for a market (P1 Tier-A).
 * BottomSheet container (kit dialog). POST on create, PATCH on edit; only
 * non-empty optional fields go to the wire (name_ky blank → null). slug is
 * lowercase-slug validated; lat/lng ride the wire as Decimal STRINGS (validated
 * as numeric strings, forwarded verbatim — never parsed to float).
 *
 * `entrance_count` (SCRUM-16x) is no longer surfaced here — it wasn't
 * meaningful to an admin managing pavilion sellers and there's no consumer
 * surface that reads it. The backend field is untouched (`MarketCreate`
 * defaults it to 1, `MarketUpdate` treats an omitted value as "don't touch"),
 * so simply not sending it is wire-compatible.
 *
 * Location section (SCRUM-158): a `MarketMapPicker` 2GIS map sits above the
 * Lat/Lng fields — clicking the map calls `setValue` on both (validated +
 * dirtied), so a picked point flows through the exact same Zod-validated RHF
 * fields a manually-typed coordinate would. The Lat/Lng `Field`s remain fully
 * editable — the map is an additional input, not a replacement.
 *
 * Delete (SCRUM-16x): lives INSIDE this dialog's own footer, not as a sibling
 * element on the page behind it — the previous placement sat in the page's
 * normal flow underneath the BottomSheet's backdrop/stacking context, so it
 * was visible but unclickable while open, and unmounted (not just disabled)
 * once the dialog closed. `onRequestDelete` hands the confirm step back to
 * the caller (`MarketListPage` owns the confirm BottomSheet).
 */
import { useCallback, type ReactElement } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import { BottomSheet, Button, Field } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useCreateMarket, useUpdateMarket } from "@/features/admin/markets/hooks";
import { MarketMapPicker } from "@/features/admin/markets/MarketMapPicker";
import type { AdminMarket } from "@/features/admin/markets/api";

const schema = z.object({
  slug: z.string().min(1, "formError.required").max(128).regex(/^[a-z0-9-]+$/, "formError.slug"),
  name_ru: z.string().min(1, "formError.required").max(200),
  name_ky: z.string().max(200),
  city: z.string().min(1, "formError.required").max(128),
  address: z.string().min(1, "formError.required").max(500),
  lat: z.string().regex(/^-?\d+(\.\d+)?$/, "formError.numeric"),
  lng: z.string().regex(/^-?\d+(\.\d+)?$/, "formError.numeric"),
});
type FormValues = z.infer<typeof schema>;

export interface MarketFormDialogProps {
  open: boolean;
  market: AdminMarket | null;
  onClose: () => void;
  onSuccess: () => void;
  /** Present only when editing — renders a Delete button in the footer. */
  onRequestDelete?: (market: AdminMarket) => void;
}

export function MarketFormDialog({
  open,
  market,
  onClose,
  onSuccess,
  onRequestDelete,
}: MarketFormDialogProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const create = useCreateMarket();
  const update = useUpdateMarket();
  const isEdit = market !== null;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      slug: market?.slug ?? "",
      name_ru: market?.name_ru ?? "",
      name_ky: market?.name_ky ?? "",
      city: market?.city ?? "",
      address: market?.address ?? "",
      lat: market?.lat ?? "", // already a Decimal string off the wire
      lng: market?.lng ?? "",
    },
  });

  const errText = (key?: string): string | undefined => (key ? t(key) : undefined);

  const submit = useCallback(
    (values: FormValues) => {
      const body = {
        slug: values.slug.trim(),
        name_ru: values.name_ru.trim(),
        name_ky: values.name_ky.trim() === "" ? null : values.name_ky.trim(),
        city: values.city.trim(),
        address: values.address.trim(),
        lat: values.lat.trim(), // Decimal as STRING — forwarded verbatim
        lng: values.lng.trim(),
      };
      if (isEdit && market) {
        update.mutate(
          { id: market.id, body },
          { onSuccess: () => { toast.success(t("markets.form.updatedToast")); onSuccess(); }, onError: () => toast.error(t("error.generic")) },
        );
      } else {
        create.mutate(body, {
          onSuccess: () => { toast.success(t("markets.form.createdToast")); onSuccess(); },
          onError: () => toast.error(t("error.generic")),
        });
      }
    },
    [isEdit, market, create, update, onSuccess, t],
  );

  const isPending = create.isPending || update.isPending;

  return (
    <BottomSheet open={open} onClose={onClose} title={t(isEdit ? "markets.form.editTitle" : "markets.form.createTitle")}>
      <form data-testid="market-form" onSubmit={handleSubmit(submit)} className="flex flex-col gap-4">
        <Field label={t("markets.form.slug")} data-testid="market-form-slug" error={errText(errors.slug?.message)} {...register("slug")} />
        <Field label={t("markets.form.nameRu")} data-testid="market-form-name_ru" error={errText(errors.name_ru?.message)} {...register("name_ru")} />
        <Field label={t("markets.form.nameKy")} data-testid="market-form-name_ky" {...register("name_ky")} />
        <Field label={t("markets.form.city")} data-testid="market-form-city" error={errText(errors.city?.message)} {...register("city")} />
        <Field label={t("markets.form.address")} data-testid="market-form-address" error={errText(errors.address?.message)} {...register("address")} />

        <div className="flex flex-col gap-2">
          <span className="text-[13px] font-semibold text-foreground">{t("markets.form.map.label")}</span>
          <MarketMapPicker
            lat={market?.lat ?? ""}
            lng={market?.lng ?? ""}
            onPick={(nextLat, nextLng) => {
              setValue("lat", nextLat, { shouldValidate: true, shouldDirty: true });
              setValue("lng", nextLng, { shouldValidate: true, shouldDirty: true });
            }}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t("markets.form.lat")} data-testid="market-form-lat" inputMode="decimal" error={errText(errors.lat?.message)} {...register("lat")} />
          <Field label={t("markets.form.lng")} data-testid="market-form-lng" inputMode="decimal" error={errText(errors.lng?.message)} {...register("lng")} />
        </div>
        <div className="flex items-center justify-between gap-2">
          {isEdit && market && onRequestDelete ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              data-testid="market-delete-open"
              onClick={() => onRequestDelete(market)}
              disabled={isPending}
              className="min-h-[44px]"
            >
              {t("markets.delete.action")}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={isPending} className="min-h-[44px]">{t("common.cancel")}</Button>
            <Button type="submit" variant="primary" size="sm" data-testid="market-form-submit" loading={isPending} className="min-h-[44px]">{t("markets.form.submit")}</Button>
          </div>
        </div>
      </form>
    </BottomSheet>
  );
}

export default MarketFormDialog;
